//
// # Wires
// ("Manager" maybe, but nah.)
//

// Local Imports
import { logger } from "CoreUtils";
import { Point, WireSegment as WireSegmentData, randomId } from "GenAlphaModel";
import { WireSegment } from "./wire";
import * as changes from "../../changes";
import { type SchematicRender } from "./render";
import { pointKey, getSchPinWorldLocation } from "../../routing/wire_utils";

// Type alias for changes to wire segments
// Note we never `Update` these, only `Add` and `Remove`.
// We do call `WireSegment.update()`, but only on "provisional" wires that are in the process of being added.
type WireChange = changes.Add<WireSegment> | changes.Remove<WireSegment>;

// `changes.Combo` doesnt quite work here for... reasons
interface WireCombo {
  first: WireChange;
  rest: Array<WireChange>;
}

const { Add, Remove } = changes;

export class Wires {
  // The render tier that owns this index: provides the entity-tree parent for
  // new WireSegments and the instance/pin sets `pinPointKeys` reads.
  constructor(private host: SchematicRender) {}

  // Change-logging callback, injected by the interaction facade (`Schematic`)
  // after construction. Left undefined on the render-only (server) path, where
  // no edit — and so no logging — ever happens.
  onLogChange?: (change: changes.Change<WireSegment>) => void;

  horiz = new Map<number, Set<WireSegment>>();
  vert = new Map<number, Set<WireSegment>>();
  nonManhattan = new Set<WireSegment>();

  // How many times `rawDelete` failed to find a wire in its bucket.
  //
  // Non-zero means a code path mutated endpoints without going through
  // `WireSegment.update()`, so the index no longer agrees with the geometry.
  // `Editor/AGENTS.md` forbids throwing in `src/`, and the caller ignores the returned
  // false, so a log line was the only trace — and nothing reads a log line.
  // A count is assertable: tests exercising the drag pipeline check it is zero.
  indexMisses = 0;

  // Provisional wire segments — in-progress drawing, not yet committed to horiz/vert/nonManhattan.
  // Tracked here so that `all()` includes them for rendering.
  provisionals = new Set<WireSegment>();

  // Create and track a provisional wire segment.
  addProvisional = (data: WireSegmentData): WireSegment => {
    const seg = this.provisional(data);
    this.provisionals.add(seg);
    return seg;
  };

  // Remove all provisional segments from tracking (before committing them).
  clearProvisionals = () => {
    this.provisionals.clear();
  };

  // Remove a single provisional segment from tracking.
  removeProvisional = (seg: WireSegment) => {
    this.provisionals.delete(seg);
    seg.abort();
  };

  // Abort all provisional segments and clear them.
  abortProvisionals = () => {
    this.provisionals.forEach((seg) => {
      seg.abort();
    });
    this.provisionals.clear();
  };

  // Set of all wire segments
  all = (): Set<WireSegment> => {
    const all = new Set(this.nonManhattan);
    for (const set of this.horiz.values()) {
      for (const wire of set) all.add(wire);
    }
    for (const set of this.vert.values()) {
      for (const wire of set) all.add(wire);
    }
    for (const wire of this.provisionals) {
      all.add(wire);
    }
    return all;
  };

  // Array of all wire segments
  values = (): Array<WireSegment> => Array.from(this.all());

  // Add a wire segment, logging what the merge did as one change.
  add = (arg: WireSegment | Array<WireSegment>, logChange: boolean) => {
    // When logChange is false (undo/redo), bypass split/merge and insert directly.
    if (!logChange) {
      if (Array.isArray(arg)) {
        arg.forEach((wire) => {
          this.rawAdd(wire);
        });
      } else {
        this.rawAdd(arg);
      }
      return;
    }
    const combo = this.insertMerging(arg);
    if (combo) {
      this.onLogChange?.(changes.Combo.new(combo.first, ...combo.rest));
    }
  };

  // The merge/split insert itself. Returns the changes it made, or null for an
  // empty batch.
  private insertMerging = (
    arg: WireSegment | Array<WireSegment>
  ): WireCombo | null => {
    // Pin locations are stable within a single add() dispatch, so build the
    // set at most once, shared across all wires in the batch — and only if a
    // wire actually merges (addVert/addHoriz call this lazily).
    let pinKeysCache: Set<string> | undefined;
    const getPinKeys = () => (pinKeysCache ??= this.pinPointKeys());

    const inner = (wire: WireSegment) => {
      const start = wire.start();
      const end = wire.end();

      if (start.x === end.x) {
        const vert: Vert = {
          x: start.x,
          y: Range.new(start.y, end.y),
          seg: wire,
        };
        return this.addVert(vert, getPinKeys);
      }

      if (start.y === end.y) {
        const horiz: Horiz = {
          y: start.y,
          x: Range.new(start.x, end.x),
          seg: wire,
        };
        return this.addHoriz(horiz, getPinKeys);
      }

      // Non-manhattan wire
      this.nonManhattan.add(wire);
      return { first: Add.new(wire), rest: [] };
    };

    let change: WireCombo;
    if (Array.isArray(arg)) {
      // Guard against empty arrays
      if (arg.length === 0) {
        return null;
      }
      const [first, ...rest] = arg;
      change = inner(first);
      for (const wire of rest) {
        const subChange = inner(wire);
        change.rest.push(subChange.first, ...subChange.rest);
      }
    } else {
      // Single wire case
      change = inner(arg);
    }
    return change;
  };

  // Insert through the same merge/split path as `add`, but hand the resulting
  // changes back instead of logging them.
  //
  // A caller making several edits in one gesture needs them in its own combo,
  // or the gesture takes more than one undo to reverse. Without this the only
  // way to insert without logging was the raw path meant for undo/redo replay,
  // which skips the merge entirely and leaves overlapping geometry behind.
  addMerging = (
    arg: WireSegment | Array<WireSegment>
  ): Array<changes.Single<WireSegment>> => {
    const combo = this.insertMerging(arg);
    if (!combo) return [];
    return [combo.first, ...combo.rest];
  };

  // Add a Vertical wire
  private addVert = (vert: Vert, getPinKeys: () => Set<string>): WireCombo => {
    const { x, y } = vert;
    let wire = vert.seg;
    // Captured before any merge widens `y`.
    const addedMin = y.min;
    const addedMax = y.max;

    const changeList = [
      ...this.splitHorizontalsAt(x, y.min),
      ...this.splitHorizontalsAt(x, y.max),
    ];
    const rv: WireCombo = { first: Add.new(wire), rest: changeList };

    //
    // Check for any existing segments, which we may need to merge with
    const existing = this.vert.get(x);
    if (!existing) {
      // No existing wires at that Y coordinate. Create a new set.
      this.vert.set(x, new Set([wire]));
      return rv;
    }

    // Check the existing segments for any that can be merged with this one
    const intersecting = new Set<Vert>();

    for (const seg of existing) {
      const segStart = seg.start();
      const segEnd = seg.end();
      // Check whether the segment intersects.
      // Note `start` and `end` are not ordered.
      const segRange = Range.new(segStart.y, segEnd.y);
      if (y.intersects(segRange)) {
        y.expand(segRange);
        intersecting.add({ x, y: segRange, seg });
      }
    }

    // Add our new segment to the set of existing ones
    existing.add(wire);

    // And if it doesn't intersect any others, we're done.
    if (!intersecting.size) {
      return rv;
    }

    // We found some intersection. Start merging.

    // First expand our new wire to cover all intersecting segments
    wire.update({
      endpoints: [
        { x, y: y.min },
        { x, y: y.max },
      ],
    });

    // Sort intersecting segments by increasing x-coordinate
    const ordered = Array.from(intersecting).sort((a, b) => a.y.min - b.y.min);

    // Connection points that must survive the merge (pins). See pinPointKeys.
    const pinKeys = getPinKeys();

    // The incoming wire's own ends are absorbed endpoints too. See the matching
    // note in `addHoriz`: a wire laid inside a longer collinear one contributes
    // no boundary of its own, so an end of it resting on a pin dissolved into
    // the merged span.
    const ownEnds = [addedMin, addedMax]
      .filter((c) => c > y.min && c < y.max)
      .sort((a, b) => a - b);
    let released = 0;

    for (const inter of ordered) {
      //
      // Split at this coordinate if it is an electrical connection point: an
      // opposite-axis wire ends here (T-junction) or a pin sits here. Otherwise
      // the absorbed endpoint dissolves into the interior of the merged wire.
      const maybeSplit = (atY: number) => {
        // Never split at or before the running wire's current start: two
        // absorbed segments can share a coordinate (e.g. both ending on a pin),
        // and a second split there would produce a zero-length segment — which
        // then renders as a spurious junction dot. Split coordinates arrive in
        // ascending order, so a repeat can only be at the start.
        if (atY <= wire.data.endpoints[0].y) return;
        if (
          !this.doAnyHorizWiresEndAt(x, atY) &&
          !pinKeys.has(pointKey({ x, y: atY }))
        )
          return;

        // If so, split the new wire in two. This will generally create a Dot.
        wire.update({
          endpoints: [Point.clone(wire.data.endpoints[0]), { x, y: atY }],
        });
        wire = this.provisional({
          endpoints: [
            { x, y: atY },
            { x, y: y.max },
          ],
          style: wire.data.style,
          labels: [],
        });
        existing.add(wire);
        changeList.push(changes.Add.new(wire));
      };

      const releaseUpTo = (limit: number) => {
        while (released < ownEnds.length && ownEnds[released] <= limit) {
          maybeSplit(ownEnds[released++]);
        }
      };

      // If necessary, split at its min point
      releaseUpTo(inter.y.min);
      if (inter.y.min !== y.min) maybeSplit(inter.y.min);
      // Add its labels before potentially splitting at its max point
      wire.data.labels.push(...inter.seg.data.labels);
      // And if necessary, split at its max point
      releaseUpTo(inter.y.max);
      if (inter.y.max !== y.max) maybeSplit(inter.y.max);
      if (inter === ordered[ordered.length - 1]) releaseUpTo(y.max);

      // Remove the old segment
      const { seg } = inter;
      existing.delete(seg);
      changeList.push(changes.Remove.new(seg));
    }

    // Note we always add and update the *new* segments, removing any past intersecting ones,
    // and never modifying any in the existing data stores.
    return rv;
  };

  private joinVerticalAt = (at: Point): Array<WireChange> => {
    const { x, y } = at;
    const existing = this.vert.get(x);
    if (!existing) return [];

    const toBeJoined = Array.from(existing).filter(
      (wire) => wire.start().y === y || wire.end().y === y
    );
    if (toBeJoined.length < 2) return []; // Nothing to join, done.

    // We've got a hit. Join it here.
    // Collect up all the labels in a single list
    const labels = [];
    for (const wire of toBeJoined) {
      labels.push(...wire.data.labels);
    }
    // FIXME: this should also probably only happen if the path-styles are the same? Or at least widths?
    const firstStyle = toBeJoined[0].data.style;

    // Get the min and max Y coordinates to join
    const min = Math.min(
      ...toBeJoined.map((wire) => wire.start().y),
      ...toBeJoined.map((wire) => wire.end().y)
    );
    const max = Math.max(
      ...toBeJoined.map((wire) => wire.start().y),
      ...toBeJoined.map((wire) => wire.end().y)
    );

    const changes = [];

    // Creata a new wire segment
    const newWire = this.provisional({
      endpoints: [
        { x, y: min },
        { x, y: max },
      ],
      labels,
      style: firstStyle,
    });
    existing.add(newWire);
    changes.push(Add.new(newWire));

    // Remove all the existing segments
    toBeJoined.forEach((w) => {
      existing.delete(w);
      changes.push(Remove.new(w));
    });

    return changes;
  };

  private joinHorizAt = (at: Point): Array<WireChange> => {
    const { x, y } = at;
    const existing = this.horiz.get(y);
    if (!existing) return [];

    const toBeJoined = Array.from(existing).filter(
      (wire) => wire.start().x === x || wire.end().x === x
    );
    if (toBeJoined.length < 2) return []; // Nothing to join, done.

    // We've got a hit. Join it here.
    // Collect up all the labels in a single list
    const labels = [];
    for (const wire of toBeJoined) {
      labels.push(...wire.data.labels);
    }
    // FIXME: this should also probably only happen if the path-styles are the same? Or at least widths?
    const firstStyle = toBeJoined[0].data.style;

    // Get the min and max Y coordinates to join
    const min = Math.min(
      ...toBeJoined.map((wire) => wire.start().x),
      ...toBeJoined.map((wire) => wire.end().x)
    );
    const max = Math.max(
      ...toBeJoined.map((wire) => wire.start().x),
      ...toBeJoined.map((wire) => wire.end().x)
    );

    const changes = [];

    // Creata a new wire segment
    const newWire = this.provisional({
      endpoints: [
        { x: min, y },
        { x: max, y },
      ],
      labels,
      style: firstStyle,
    });
    existing.add(newWire);
    changes.push(Add.new(newWire));

    // Remove all the existing segments
    toBeJoined.forEach((w) => {
      existing.delete(w);
      changes.push(Remove.new(w));
    });

    return changes;
  };

  private splitVerticalsAt = (x: number, y: number): Array<WireChange> => {
    const existing = this.vert.get(x);
    if (!existing) return [];

    const changeList = [];
    for (const wire of Array.from(existing)) {
      const start = wire.start();
      const end = wire.end();
      const min = Math.min(start.y, end.y);
      const max = Math.max(start.y, end.y);

      // Note the test here is not inclusive -
      // hits at the endpoints do not generate a split.
      if (min < y && y < max) {
        // We've got a hit. Split it here.

        // Remove the old segment
        existing.delete(wire);
        changeList.push(Remove.new(wire));

        // Add two new ones
        const { style } = wire.data;

        // FIXME: the `labels` implicitly stick with the top one.
        // We probably wanna add, like, a distance test for them?
        const newTop = this.provisional({
          endpoints: [
            { x, y },
            { x, y: max },
          ],
          style,
          labels: wire.data.labels,
        });
        existing.add(newTop);
        changeList.push(changes.Add.new(newTop));

        const newBot = this.provisional({
          endpoints: [
            { x, y: min },
            { x, y },
          ],
          style,
          labels: [], // FIXME: None here
        });
        existing.add(newBot);
        changeList.push(changes.Add.new(newBot));
      }
    }
    return changeList;
  };

  private splitHorizontalsAt = (x: number, y: number): Array<WireChange> => {
    const existing = this.horiz.get(y);
    if (!existing) return [];

    const changeList = [];
    for (const wire of Array.from(existing)) {
      const start = wire.start();
      const end = wire.end();
      const min = Math.min(start.x, end.x);
      const max = Math.max(start.x, end.x);

      // Note the test here is not inclusive -
      // hits at the endpoints do not generate a split.
      if (min < x && x < max) {
        // We've got a hit. Split it here.

        // Remove the old segment
        existing.delete(wire);
        changeList.push(Remove.new(wire));

        // Add two new ones
        const { style } = wire.data;

        // FIXME: the `labels` implicitly stick with the top one.
        // We probably wanna add, like, a distance test for them?
        const newTop = this.provisional({
          endpoints: [
            { x, y },
            { x: max, y },
          ],
          style,
          labels: wire.data.labels,
        });
        existing.add(newTop);
        changeList.push(changes.Add.new(newTop));

        const newBot = this.provisional({
          endpoints: [
            { x: min, y },
            { x, y },
          ],
          style,
          labels: [], // FIXME: None here
        });
        existing.add(newBot);
        changeList.push(changes.Add.new(newBot));
      }
    }
    return changeList;
  };

  // Add a Horizontal wire
  private addHoriz = (
    horiz: Horiz,
    getPinKeys: () => Set<string>
  ): WireCombo => {
    const { x, y } = horiz;
    let wire = horiz.seg;
    // Captured before any merge widens `x`.
    const addedMin = x.min;
    const addedMax = x.max;

    const changeList = [
      ...this.splitVerticalsAt(x.min, y),
      ...this.splitVerticalsAt(x.max, y),
    ];
    const rv: WireCombo = { first: Add.new(wire), rest: changeList };

    //
    // Check for any existing segments, which we may need to merge with
    const existing = this.horiz.get(y);
    if (!existing) {
      // No existing wires at that Y coordinate. Create a new set.
      this.horiz.set(y, new Set([wire]));
      return rv;
    }

    // Check the existing segments for any that can be merged with this one
    const intersecting = new Set<Horiz>();

    for (const seg of existing) {
      const segStart = seg.start();
      const segEnd = seg.end();
      // Check whether the segment intersects.
      // Note `start` and `end` are not ordered.
      const segRange = Range.new(segStart.x, segEnd.x);
      if (x.intersects(segRange)) {
        x.expand(segRange);
        intersecting.add({ y, x: segRange, seg });
      }
    }

    // Add our new segment to the set of existing ones
    existing.add(wire);

    // And if it doesn't intersect any others, we're done.
    if (!intersecting.size) {
      return rv;
    }

    // We found some intersection. Start merging.
    //
    // First expand our new wire to cover all intersecting segments
    wire.update({
      endpoints: [
        { x: x.min, y },
        { x: x.max, y },
      ],
    });

    // Sort intersecting segments by increasing x-coordinate
    const ordered = Array.from(intersecting).sort((a, b) => a.x.min - b.x.min);

    // Connection points that must survive the merge (pins). See pinPointKeys.
    const pinKeys = getPinKeys();

    // The incoming wire's own ends are absorbed endpoints too, and need the
    // same protection as the segments it merges with.
    //
    // They can go missing entirely. A wire laid inside a longer collinear one
    // widens `x` to that wire's range, so neither of the boundary tests below
    // fires and `ordered` offers no coordinate of its own — an end of it
    // resting on a pin dissolved into the merged span and took the pin off the
    // net. Held in ascending order and released as the sweep passes them,
    // since `maybeSplit` only ever moves forwards.
    const ownEnds = [addedMin, addedMax]
      .filter((c) => c > x.min && c < x.max)
      .sort((a, b) => a - b);
    let released = 0;

    for (const inter of ordered) {
      //
      // Split at this coordinate if it is an electrical connection point: an
      // opposite-axis wire ends here (T-junction) or a pin sits here. Otherwise
      // the absorbed endpoint dissolves into the interior of the merged wire.
      const maybeSplit = (atX: number) => {
        // Never split at or before the running wire's current start: two
        // absorbed segments can share a coordinate (e.g. both ending on a pin),
        // and a second split there would produce a zero-length segment — which
        // then renders as a spurious junction dot. Split coordinates arrive in
        // ascending order, so a repeat can only be at the start.
        if (atX <= wire.data.endpoints[0].x) return;
        if (
          !this.doAnyVerticalWiresEndAt(atX, y) &&
          !pinKeys.has(pointKey({ x: atX, y }))
        )
          return;

        // If so, split the new wire in two. This will generally create a Dot.
        wire.update({
          endpoints: [Point.clone(wire.data.endpoints[0]), { x: atX, y }],
        });
        wire = this.provisional({
          endpoints: [
            { x: atX, y },
            { x: x.max, y },
          ],
          style: wire.data.style,
          labels: [],
        });
        existing.add(wire);
        changeList.push(changes.Add.new(wire));
      };

      const releaseUpTo = (limit: number) => {
        while (released < ownEnds.length && ownEnds[released] <= limit) {
          maybeSplit(ownEnds[released++]);
        }
      };

      // If necessary, split at its min point
      releaseUpTo(inter.x.min);
      if (inter.x.min !== x.min) maybeSplit(inter.x.min);
      // Add its labels before potentially splitting at its max point
      wire.data.labels.push(...inter.seg.data.labels);
      // And if necessary, split at its max point
      releaseUpTo(inter.x.max);
      if (inter.x.max !== x.max) maybeSplit(inter.x.max);
      if (inter === ordered[ordered.length - 1]) releaseUpTo(x.max);

      // Remove the old segment
      const { seg } = inter;
      existing.delete(seg);
      changeList.push(changes.Remove.new(seg));
    }

    return rv;
  };

  doAnyVerticalWiresEndAt = (x: number, y: number): boolean => {
    const existing = this.vert.get(x);
    if (!existing) return false;
    return Array.from(existing).some(
      (wire) => wire.start().y === y || wire.end().y === y
    );
  };

  doAnyHorizWiresEndAt = (x: number, y: number): boolean => {
    const existing = this.horiz.get(y);
    if (!existing) return false;
    return Array.from(existing).some(
      (wire) => wire.start().x === x || wire.end().x === x
    );
  };

  // Point keys of every instance pin and schematic pin in the parent schematic.
  // A wire endpoint landing on one of these is an electrical connection point:
  // the pin's net is anchored by a wire *endpoint* inside its hit box (see the
  // Rust extractor's `find_net_hitting_bbox`), so if a merge dissolves that
  // endpoint into the interior of a longer wire the pin silently disconnects.
  // We rebuild this per add() rather than cache it, since pins move/change.
  pinPointKeys = (): Set<string> => {
    const keys = new Set<string>();
    for (const instance of this.host.instances) {
      for (const loc of instance.getPinWorldLocations()) {
        keys.add(pointKey(loc));
      }
    }
    for (const schPin of this.host.pins) {
      keys.add(pointKey(getSchPinWorldLocation(schPin)));
    }
    return keys;
  };

  // Merge collinear overlapping provisional segments into minimal non-overlapping segments.
  // This prevents self-merges inside add() which create contradictory
  // Add/Remove pairs in the change log, breaking undo/redo.
  //
  // `protectedKeys` are connection points (pin locations) whose vertex must not
  // be dissolved: collinear segments meeting at one keep an endpoint there, so
  // the pin stays anchored. Pass `wires.pinPointKeys()`. That holds for
  // overlapping segments too, not just end-to-end ones: a merged span is split
  // back apart at every protected coordinate that was an endpoint inside it.
  //
  // Splitting can need more segments than the merge had (a long wire crossing a
  // short one pinned at both ends). `createSegment` supplies the extra ones; a
  // caller that cannot make segments may omit it, and such a group is then left
  // unmerged — redundant overlap is recoverable, a disconnected pin is not.
  static mergeCollinear(
    segments: Array<WireSegment>,
    protectedKeys: Set<string> = new Set(),
    createSegment?: (
      template: WireSegment,
      endpoints: [Point, Point]
    ) => WireSegment
  ): Array<WireSegment> {
    // Group horizontal segments by Y coordinate, vertical by X coordinate
    const horizGroups = new Map<number, Array<WireSegment>>();
    const vertGroups = new Map<number, Array<WireSegment>>();
    const nonManhattan: Array<WireSegment> = [];

    for (const seg of segments) {
      const start = seg.start();
      const end = seg.end();
      if (start.y === end.y) {
        const group = horizGroups.get(start.y) ?? [];
        group.push(seg);
        horizGroups.set(start.y, group);
      } else if (start.x === end.x) {
        const group = vertGroups.get(start.x) ?? [];
        group.push(seg);
        vertGroups.set(start.x, group);
      } else {
        nonManhattan.push(seg);
      }
    }

    const result: Array<WireSegment> = [...nonManhattan];

    // Lay a merged piece back down: reuse the segment it came from, or make a
    // fresh one from its template when the split needed an extra.
    const place = (piece: MergedPiece, endpoints: [Point, Point]): void => {
      if (piece.seg) {
        piece.seg.update({ endpoints });
        result.push(piece.seg);
        return;
      }
      if (!createSegment) return; // Guarded by `canSplit` in mergeRangeGroup.
      result.push(createSegment(piece.template, endpoints));
    };

    // Merge each horizontal group
    for (const [y, group] of horizGroups) {
      const merged = mergeRangeGroup(
        group,
        (seg) => Range.new(seg.start().x, seg.end().x),
        (coord) => protectedKeys.has(pointKey({ x: coord, y })),
        createSegment !== undefined
      );
      for (const piece of merged) {
        place(piece, [
          { x: piece.range.min, y },
          { x: piece.range.max, y },
        ]);
      }
    }

    // Merge each vertical group
    for (const [x, group] of vertGroups) {
      const merged = mergeRangeGroup(
        group,
        (seg) => Range.new(seg.start().y, seg.end().y),
        (coord) => protectedKeys.has(pointKey({ x, y: coord })),
        createSegment !== undefined
      );
      for (const piece of merged) {
        place(piece, [
          { x, y: piece.range.min },
          { x, y: piece.range.max },
        ]);
      }
    }

    return result;
  }

  // Add a wire directly into the horiz/vert/nonManhattan maps without triggering
  // split/merge logic. Used by undo/redo to restore exact state.
  rawAdd = (wire: WireSegment): void => {
    const start = wire.start();
    const end = wire.end();

    if (start.x === end.x) {
      const existing = this.vert.get(start.x) ?? new Set();
      existing.add(wire);
      this.vert.set(start.x, existing);
    } else if (start.y === end.y) {
      const existing = this.horiz.get(start.y) ?? new Set();
      existing.add(wire);
      this.horiz.set(start.y, existing);
    } else {
      this.nonManhattan.add(wire);
    }
  };

  // Remove a wire from its bucket. Looks the wire up by its current endpoints,
  // which is correct as long as `WireSegment.update()` keeps the index in sync.
  // On miss, logs an error and returns false — don't recover by scanning, that
  // would hide the underlying corruption.
  rawDelete = (wire: WireSegment): boolean => {
    const start = wire.start();
    const end = wire.end();

    if (start.x === end.x) {
      if (this.vert.get(start.x)?.delete(wire)) return true;
    } else if (start.y === end.y) {
      if (this.horiz.get(start.y)?.delete(wire)) return true;
    } else if (this.nonManhattan.delete(wire)) {
      return true;
    }

    this.indexMisses++;
    logger.error(
      `Wires.rawDelete: index miss for wire id=${wire.data.id} at (${start.x},${start.y})→(${end.x},${end.y}). The bucket index has drifted from the wire's endpoints — a code path mutated endpoints without going through WireSegment.update(). Investigate; do not silently recover.`
    );
    return false;
  };

  // Create a provisional wire segment, which has enough stuff to be drawn, but is not yet part of our data structures.
  // This should probably be the only call to the `WireSegment` constructor? We think.
  provisional = (data: WireSegmentData): WireSegment => {
    return new WireSegment({
      data: { ...data, id: data.id ?? randomId() }, // Assign an ID if not already set
      parent: this.host, // parent
      wires: this, // owning index — see WireSegment.update()
    });
  };

  delete = (wire: WireSegment, logChange: boolean): boolean => {
    // When logChange is false (undo/redo), bypass join logic and remove directly.
    if (!logChange) {
      return this.rawDelete(wire);
    }

    // FIXME:
    // - Potentially merge segments that are not contiguous
    // - This will generate some `Combo` changes
    //
    const start = wire.start();
    const end = wire.end();

    // Remove from its bucket via rawDelete (shared lookup + error log).
    if (!this.rawDelete(wire)) return false;

    if (start.x === end.x) {
      // Vertical wire
      const changeList: Array<WireChange> = [
        ...this.joinHorizAt(start),
        ...this.joinHorizAt(end),
      ];
      this.onLogChange?.(changes.Combo.new(Remove.new(wire), ...changeList));
      return true;
    }
    if (start.y === end.y) {
      // Horizontal wire
      const changeList: Array<WireChange> = [
        ...this.joinVerticalAt(start),
        ...this.joinVerticalAt(end),
      ];
      this.onLogChange?.(changes.Combo.new(Remove.new(wire), ...changeList));
      return true;
    }
    // Non-manhattan wire
    this.onLogChange?.(Remove.new(wire));
    return true;
  };
}

// One piece of a merged run: the range it covers, and the segment that should
// hold it. A null `seg` means the run split into more pieces than it had
// segments and a new one must be made from `template`.
interface MergedPiece {
  seg: WireSegment | null;
  template: WireSegment;
  range: Range;
}

// Merge a group of segments with overlapping 1D ranges into minimal non-overlapping segments.
// Absorbed segments are aborted and their labels transferred to the surviving segment.
//
// `isProtected(coord)` guards a connection point (a pin). Two segments that only
// meet end-to-end at a protected coordinate are never merged in the first place.
// Overlapping ones do merge, but the merged span is then cut back apart at every
// protected coordinate that was an endpoint strictly inside it: a pin's net is
// anchored by a wire *endpoint*, so a pin left on a span is not connected.
//
// `canSplit` says whether the caller can supply extra segments. Without it, a
// run needing more pieces than it has segments is returned unmerged.
function mergeRangeGroup(
  segments: Array<WireSegment>,
  getRange: (seg: WireSegment) => Range,
  isProtected: (coord: number) => boolean = () => false,
  canSplit = false
): Array<MergedPiece> {
  const asPiece = (r: { seg: WireSegment; range: Range }): MergedPiece => ({
    seg: r.seg,
    template: r.seg,
    range: r.range,
  });

  if (segments.length <= 1) {
    return segments.map((seg) => asPiece({ seg, range: getRange(seg) }));
  }

  // Sort by range min
  const sorted = segments
    .map((seg) => ({ seg, range: getRange(seg) }))
    .sort((a, b) => a.range.min - b.range.min);

  // Gather the segments into runs that fuse into a single span. Ranges arrive in
  // ascending `min` order, so a run only ever grows to the right.
  const runs: Array<Array<{ seg: WireSegment; range: Range }>> = [[sorted[0]]];
  let runMax = sorted[0].range.max;

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    if (shouldMergeCollinear(runMax, current.range.min, isProtected)) {
      runs[runs.length - 1].push(current);
      runMax = Math.max(runMax, current.range.max);
    } else {
      runs.push([current]);
      runMax = current.range.max;
    }
  }

  const result: Array<MergedPiece> = [];
  for (const run of runs) {
    if (run.length === 1) {
      result.push(asPiece(run[0]));
      continue;
    }
    result.push(...resolveRun(run, isProtected, canSplit));
  }
  return result;
}

// Turn one fused run into its final pieces: the run's whole span, cut at each
// protected coordinate that was an endpoint inside it.
function resolveRun(
  run: Array<{ seg: WireSegment; range: Range }>,
  isProtected: (coord: number) => boolean,
  canSplit: boolean
): Array<MergedPiece> {
  const min = Math.min(...run.map((r) => r.range.min));
  const max = Math.max(...run.map((r) => r.range.max));

  const cuts = Array.from(
    new Set(run.flatMap((r) => [r.range.min, r.range.max]))
  )
    .filter((coord) => coord > min && coord < max && isProtected(coord))
    .sort((a, b) => a - b);

  const bounds = [min, ...cuts, max];
  const spans: Array<Range> = [];
  for (let i = 0; i + 1 < bounds.length; i++) {
    spans.push(Range.new(bounds[i], bounds[i + 1]));
  }

  // More pieces than segments and no way to make one: leave the run alone.
  // Overlapping wires are redundant but connected; a swallowed pin is not.
  if (spans.length > run.length && !canSplit)
    return run.map((r) => ({
      seg: r.seg,
      template: r.seg,
      range: r.range,
    }));

  // Hand each piece the segment that already covers it — the tightest such fit,
  // so identities and their labels stay closest to where they were.
  const used = new Set<WireSegment>();
  const pieces: Array<MergedPiece> = spans.map((range) => {
    let best: { seg: WireSegment; range: Range } | undefined;
    for (const r of run) {
      if (used.has(r.seg)) continue;
      if (r.range.min > range.min || r.range.max < range.max) continue;
      if (
        !best ||
        r.range.max - r.range.min < best.range.max - best.range.min
      ) {
        best = r;
      }
    }
    best ??= run.find((r) => !used.has(r.seg));
    if (best) used.add(best.seg);
    return { seg: best?.seg ?? null, template: run[0].seg, range };
  });

  // Segments no piece needed are absorbed: their labels move to the first piece.
  const host = pieces[0].seg;
  for (const r of run) {
    if (used.has(r.seg)) continue;
    if (host) host.data.labels.push(...r.seg.data.labels);
    r.seg.abort();
  }

  return pieces;
}

// Decide whether a following collinear segment should merge into the running
// merged range. Segments are processed in ascending `min` order, so the
// follower's `curMin` is never less than the running range's min; the two ranges
// therefore intersect exactly when `curMin <= lastMax`.
//
// The one exception: when they merely touch end-to-end (`lastMax === curMin`) at
// a protected connection point, they are kept separate. Dissolving that shared
// vertex would silently disconnect a pin anchored there, since a pin's net is
// anchored by a wire *endpoint* inside its hit box.
export function shouldMergeCollinear(
  lastMax: number,
  curMin: number,
  isProtected: (coord: number) => boolean
): boolean {
  if (curMin > lastMax) return false; // Disjoint — no merge.
  const touchesAtBoundary = curMin === lastMax;
  if (touchesAtBoundary && isProtected(curMin)) return false;
  return true;
}

class Range {
  //
  // A pair of ordered numbers, sorted at construction time, with some convenience methods.
  //
  private constructor(public min: number, public max: number) {}

  static new(a: number, b: number): Range {
    return new Range(Math.min(a, b), Math.max(a, b));
  }
  expand = (other: Range) => {
    this.min = Math.min(this.min, other.min);
    this.max = Math.max(this.max, other.max);
  };
  intersects = (other: Range): boolean => {
    return (
      (this.min <= other.min && other.min <= this.max) ||
      (this.min <= other.max && other.max <= this.max) ||
      // FIXME: ... aren't these redundant?
      (other.min <= this.min && this.min <= other.max) ||
      (other.min <= this.max && this.max <= other.max)
    );
  };
}

interface Horiz {
  y: number;
  x: Range;
  seg: WireSegment;
}
interface Vert {
  x: number;
  y: Range;
  seg: WireSegment;
}
